import { useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { deleteChargeCategory } from "@/services/chargeService";
import { romanNumeral } from "@/utils/charges";
import type { Charge, ChargeCategory } from "@/types";
import ChargeCategoryFormModal from "./ChargeCategoryFormModal";
import { plural } from "@/utils/format";

interface Props {
  categories: ChargeCategory[];
  charges: Charge[];
  onChanged: () => void;
}

export default function ChargeCategoriesPanel({ categories, charges, onChanged }: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChargeCategory | null>(null);
  const [toDelete, setToDelete] = useState<ChargeCategory | null>(null);

  const countCharges = (id: string) => charges.filter((c) => c.categoryId === id).length;

  const openForm = (category: ChargeCategory | null) => {
    setEditing(category);
    setFormOpen(true);
  };

  const requestDelete = (category: ChargeCategory) => {
    const used = countCharges(category.id);
    if (used > 0) {
      notify(`Impossible de supprimer : ${plural(used, "charge")} utilisent cette catégorie.`, "error");
      return;
    }
    setToDelete(category);
  };

  return (
    <Card>
      <SectionTitle
        icon="ri-price-tag-3-line"
        title="Catégories de charges"
        subtitle={`${plural(categories.length, "catégorie")} — regroupent les charges du tableau des décaissements`}
        action={
          <Button size="sm" variant="outline" icon="ri-add-line" onClick={() => openForm(null)} style={can("charges:create") ? undefined : { display: "none" }}>
            Ajouter
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="flex flex-col rounded-md border border-background-200 bg-background-100/50 p-4 transition-colors hover:border-background-300"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-9 h-9 rounded-md bg-background-50 border border-background-200 text-foreground-700 flex items-center justify-center shrink-0">
                  <i className={`${category.icon} text-base`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                    {romanNumeral(index)}
                  </p>
                  <p className="text-sm font-medium text-foreground-900 leading-tight">{category.name}</p>
                </div>
              </div>
            </div>

            {category.description && (
              <p className="mt-2.5 text-xs text-foreground-500 leading-relaxed">{category.description}</p>
            )}

            <div className="mt-3 flex items-center justify-between gap-2">
              <Badge tone="secondary" icon="ri-stack-line">
                {plural(countCharges(category.id), "charge")}
              </Badge>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openForm(category)}
                  className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label="Modifier la catégorie"
                  style={can("charges:update") ? undefined : { display: "none" }}
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => requestDelete(category)}
                  className="press w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                  aria-label="Supprimer la catégorie"
                  style={can("charges:delete") ? undefined : { display: "none" }}
                >
                  <i className="ri-delete-bin-6-line"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ChargeCategoryFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={onChanged}
        editing={editing}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer la catégorie"
        message={`Supprimer la catégorie « ${toDelete?.name} » ?`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteChargeCategory(toDelete.id);
            onChanged();
            notify("Catégorie supprimée.", "info");
          }
        }}
      />
    </Card>
  );
}